import { useLazyQuery, useMutation } from '@apollo/client';
import { Button, Modal, Radio, Spin, message } from 'antd';
import React, { useContext, useRef, useState } from 'react';
import { Cropper } from 'react-cropper';
import { AppContext } from '../../AppContext';
import { ZoomIn, ZoomOut } from '../../assets/svg';
import {
  CROPPER_HEIGHT,
  CROPPER_WIDTH,
  GA_EVENT,
  GA_LABEL,
} from '../../common/constants';
import { Event } from '../../common/trackEvents';
import { fileUpload } from '../../common/utils';
import { UPDATE_TENANT_LOGO } from './graphql/Mutation';
import { GET_URL } from './graphql/Queries';

const CropperModal = (props) => {
  const {
    showModal,
    setShowModal,
    filePrefix,
    refetch,
    fileData,
    src,
    setSrc,
    isTenantLogo = false,
    setImageKey,
    setImage,
    onCropDone,
    ...rest
  } = props;
  const { getCurrentUser } = useContext(AppContext);
  const currentUser = getCurrentUser();
  const cropperRef = useRef(null);
  const [fileObj, setFileObj] = useState();
  const [logoType, setLogoType] = useState('square');
  const [updateTenantLogo] = useMutation(UPDATE_TENANT_LOGO, {
    onError() {},
    onCompleted() {
      Event(GA_EVENT.EDIT_TENANT_LOGO, {
        label: GA_LABEL.EDIT_TENANT_LOGO,
        // eslint-disable-next-line no-undef
        pathname: window?.location?.href,
        user_id: currentUser?.id,
        user_name: currentUser?.name,
        tenant_id: currentUser?.tenantUser?.tenant?.id,
        tenant_name: currentUser?.tenantUser?.tenant?.organizationName,
      });
      setShowModal(false);
      setSrc('');
      if (refetch) {
        refetch();
      }
    },
  });
  const [fetchSignedUrl, { loading }] = useLazyQuery(GET_URL, {
    fetchPolicy: 'network-only',
    onCompleted: (res) => {
      const signedImageUrl = res?.getSignedPutUrl?.signedUrl;
      const imgData = fileObj.replace(/^data:image\/\w+;base64,/, '');
      const binaryData = atob(imgData);
      const byteNumbers = new Uint8Array(binaryData.length);
      for (let i = 0; i < binaryData.length; i += 1) {
        byteNumbers[i] = binaryData.charCodeAt(i);
      }
      const fileBuffer = new Blob([byteNumbers]);
      if (isTenantLogo && !setImageKey) {
        fileUpload(signedImageUrl, fileBuffer)
          .then(() => {
            updateTenantLogo({
              variables: {
                logo: res?.getSignedPutUrl?.key,
              },
            });
          })
          .catch(() => {
            message.error('Error while uploading image');
          });
      } else {
        if (setImageKey) {
          setImageKey(res?.getSignedPutUrl?.key);
        }
        fileUpload(signedImageUrl, fileBuffer).then(() => {
          setShowModal(false);
          setSrc('');
        });
      }
    },
  });
  const handleCancel = () => {
    setShowModal(false);
    setSrc('');
  };
  const onCrop = () => {
    const imageElement = cropperRef?.current;
    const cropper = imageElement?.cropper;
    setFileObj(cropper.getCroppedCanvas().toDataURL());
  };

  const handleUpload = (info) => {
    if (isTenantLogo) {
      try {
        fetchSignedUrl({
          variables: {
            data: {
              fileName: `${filePrefix}/${info?.file?.name || info?.name}`,
              contentType: info?.file?.type || info?.type,
              acl: isTenantLogo ? 'public-read' : 'private',
              isTenantModule: isTenantLogo,
            },
          },
        });
      } catch (error) {
        message.destroy();
        message.error('Error while uploading image');
      }
    } else {
      if (setImage) {
        setImage(fileObj);
      }
      if (onCropDone) {
        onCropDone(fileObj, info);
      }
      setShowModal(false);
      setSrc('');
    }
  };

  return (
    <Modal
      maskClosable={false}
      centered
      open={showModal}
      className="crop-modal"
      closable={false}
      onCancel={handleCancel}
      onOk={() => {
        handleUpload(fileData);
      }}
      okButtonProps={{ loading }}
    >
      <div>
        <h2>Crop Image</h2>
        <div className="d-flex align-center mb-10">
          Logo Type:
          <Radio.Group
            className="d-flex ml-5 agency-type-radio justify-end"
            defaultValue={logoType}
            onChange={(e) => {
              cropperRef.current?.cropper.setAspectRatio(
                e?.target?.value === 'square'
                  ? 1 / 1
                  : CROPPER_WIDTH / CROPPER_HEIGHT,
              );
              setLogoType(e?.target?.value);
              setSrc(src);
            }}
          >
            <Radio value="square">Square</Radio>
            <Radio value="rectangle">Rectangle</Radio>
          </Radio.Group>
        </div>
        <Spin spinning={!cropperRef?.current}>
          {src && (
            <Cropper
              ref={cropperRef}
              src={src}
              style={{ height: 200 }}
              crop={onCrop}
              responsive
              movable
              zoomable
              zoomOnTouch
              zoomOnWheel
              aspectRatio={1 / 1}
              dragMode="move"
              {...rest}
            />
          )}
          <div className="mt-25 d-flex justify-center zoom-controls">
            <Button
              className="mr-10 b-0"
              icon={<ZoomIn />}
              onClick={() => cropperRef?.current?.cropper?.zoom(0.1)}
            />
            <Button
              className="b-0"
              icon={<ZoomOut />}
              onClick={() => cropperRef?.current?.cropper?.zoom(-0.1)}
            />
          </div>
        </Spin>
      </div>
    </Modal>
  );
};

export default CropperModal;
